// MapVehPosListRepeat.js
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Box } from '@mui/system';
import BedtimeIcon from '@mui/icons-material/Bedtime'; // No report at all.
import CellTowerIcon from '@mui/icons-material/CellTower'; // cell position
import GpsOffIcon from '@mui/icons-material/GpsOff'; // no position report 1 day or more
import LocalParkingIcon from '@mui/icons-material/LocalParking'; // no position rep 0.5 hour or more
import GpsFixedIcon from '@mui/icons-material/GpsFixed'; // speed < 2
import NavigationIcon from '@mui/icons-material/Navigation'; // speed >= 2
import StartIcon from '@mui/icons-material/Start';
import TimelineIcon from '@mui/icons-material/Timeline'; // route mode
import NearMeIcon from '@mui/icons-material/NearMe'; // 지정한 차량 계속 추적.
import NearMeDisabledIcon from '@mui/icons-material/NearMeDisabled'; // 추적 중지.
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // 도움말
import { useReactiveVar } from '@apollo/client';
import { RtdTermPosDictionary } from './RtdTermPosDict';
import { modalWorkRepo, userInfoRepo, vehPosFilteredRepo, vehPosSearchRepo } from '../../value/AppModel';
import SearchBox from '../../common/SearchBox';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { useSnackbar } from 'notistack';
import Util from '../../value/Util';
import { getFloatingInfoBoxHeight } from '../map/MapFloatingInfo';
import { IconButton, Popover } from '@mui/material';

const VehPosListContainer = styled(Box)({
    flexGrow:1, display:'flex', flexDirection:'column'
});

const HeaderBox = styled(Box)({
    padding:5, display:'flex', alignItems:'center', // backgroundColor:'white',
});

const SearchBoxContainer = styled(Box)({
    flexGrow:1, display:'flex', alignItems:'center'
});

const FloatingInfoOption = {
    width: 520, // box width
    // rowHeight: 40,
};
const FloatingInfoProps = RtdTermPosDictionary.floatingInfoProps;

/**
 * 일반 업체(청소차량 제외)의 지도용 위치목록.
 * 위치데이터는 repository에 저장해서 rendering시기가 조회 주기에 영향을 미치지 않도록 한다.
 */
export default function MapVehPosListRepeat({
    mapShell,
    onClickShowRoute,
    onMouseEnterIndexCell, // (item, props, option, y)
}) {
	const sessionInfo = useReactiveVar(userInfoRepo);
    // const v3VehicleLocationListData = useReactiveVar(v3VehicleLocationListRepo);
    const vehPosList = useReactiveVar(vehPosFilteredRepo);
    const search = useReactiveVar(vehPosSearchRepo);
    const modalWork = useReactiveVar(modalWorkRepo);
    const [itemSelected, setItemSelected] = useState(null);
    const [helpAnchorEl, setHelpAnchorEl] = useState(null);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const floatingInfoBoxHeight = getFloatingInfoBoxHeight(FloatingInfoProps, FloatingInfoOption);

    const onClickRow = (item) => {
        setItemSelected(item);
        mapShell.setVehPosSelected(item);
    };

    const onChangeSearchText = (text) => {
        // 목록과 지도상에 똑같은 데이터를 보여주기 위해 repository에 저장한다. MapMain에서 필터작업이 이루어진다.
        vehPosSearchRepo(text);
    };

    const onMouseEnterTableIndexCell = (item, e) => {
        if(!item) onMouseEnterIndexCell(null);
        else {
            const windowHeight = window.innerHeight - 10;
            let y = e.clientY; // + AppNumber.MapHeaderHeight;
            if(y + floatingInfoBoxHeight > windowHeight) {
                y = windowHeight - floatingInfoBoxHeight;
            }
            onMouseEnterIndexCell(item, FloatingInfoProps, FloatingInfoOption, y);
        }
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.ROUTE:
                onClickShowRoute(row); // sets modalWorkRepo
                break;
            case AppObject.command.TRACE:
                setItemSelected(row);
                enqueueSnackbar(`지금부터 ${row.vehAlias} 차량을 계속 추적합니다.`, {variant:'info', autoHideDuration:3000});
                modalWorkRepo({vehInfoSeqToTrace:row.vehInfoSeq, vehPlates:row.vehPlates, vehAlias:row.vehAlias});
                break;
            case AppObject.command.TRACEOFF:
                enqueueSnackbar(`${row.vehAlias} 차량 추적을 중지합니다.`, {variant:'info', autoHideDuration:2000});
                modalWorkRepo({});
                break;
            case AppObject.command.CMD_SET_START:
                Util.bubbleNaverRoutePoint({isStart:true, data:{lat:row.lat, lon:row.lon, address: row.location}});
                break;
            default: return;
        }
    };

    // 점포사용자는 경로보기를 할 수 없다.
    const tools = [
        // {id: AppObject.command.ROUTE, icon: <TimelineIcon />, title:'경로보기',},
        {
            id: AppObject.command.TRACE, icon: <NearMeIcon />, title:'계속 추적하기',
            where: (row)=>row.vehInfoSeq!==modalWork.vehInfoSeqToTrace,
            color: AppStyle.mui.secondary
        },
        {
            id: AppObject.command.TRACEOFF, icon: <NearMeDisabledIcon />, title:'추적 중지',
            where: (row)=>row.vehInfoSeq===modalWork.vehInfoSeqToTrace,
        },
        {id:AppObject.command.CMD_SET_START, icon: <StartIcon />, title:'경로탐색 출발지로 설정',},
    ];
    if(!sessionInfo.isStoreUser())
        tools.unshift({id: AppObject.command.ROUTE, icon: <TimelineIcon />, title:'경로보기',});

    return(
        <VehPosListContainer>
            <HeaderBox>
                <SearchBoxContainer>
                    <SearchBox
                        value={search}
                        onChangeText={onChangeSearchText}
                        width={140}
                    />
                </SearchBoxContainer>
                <IconButton
                    title='도움말'
                    color={AppStyle.mui.primary}
                    onClick={(e)=>setHelpAnchorEl(e.currentTarget)}
                >
                    <HelpOutlineIcon />
                </IconButton>
                <Popover
                    open={Boolean(helpAnchorEl)}
                    anchorEl={helpAnchorEl}
                    onClose={()=>setHelpAnchorEl(null)}
                    anchorOrigin={{vertical:'bottom', horizontal:'center'}}
                    transformOrigin={{vertical:'top', horizontal:'center'}}
                >
                    <Box
                        style={{
                            padding:10, fontSize:'0.85rem', borderRadius:5,
                            border: '1px solid ' + AppStyle.PrimaryRGB,
                        }}
                    >
                        <p style={{backgroundColor:'#eee', padding:10}}>
                            차량목록의 각 행으로 마우스를 옮기면 행의 오른쪽에 아이콘들이 나타납니다.
                        </p>
                        {
                            !sessionInfo.isStoreUser() &&
                            <p>
                                <TimelineIcon style={{marginRight:20}}
                                    color={AppStyle.mui.primary}
                                />
                                경로보기 상자를 엽니다.
                            </p>}
                        <p>
                            <NearMeIcon style={{marginRight:20}}
                                color={AppStyle.mui.primary}
                            />
                            지정한 차량이 중심에 나타나도록 계속 추적합니다.
                        </p>
                        <p>
                            <NearMeDisabledIcon style={{marginRight:20}}
                                color={AppStyle.mui.primary}
                            />
                            추적 중인 차량의 추적을 중지합니다.
                        </p>
                        <p>
                            <StartIcon style={{marginRight:20}}
                                color={AppStyle.mui.primary}
                            />
                            선택한 차량의 위치를 경로탐색 출발지로 설정합니다.
                            상단에 탐색을 위한 상자가 나타납니다.
                        </p>
                        <p style={{backgroundColor:'#eee', padding:10}}>차량의 상태표시 기호</p>
                        <ul>
                            <li>
                                <BedtimeIcon
                                    color={AppStyle.mui.disabled}
                                    fontSize={AppWord.SMALL}
                                    style={{marginRight:10}}
                                />
                                위치보고 기록이 없음
                            </li>
                            <li>
                                <GpsOffIcon
                                    color={AppStyle.mui.warning}
                                    fontSize={AppWord.SMALL}
                                    style={{marginRight:10}}
                                />
                                5일 이상 보고기록이 없음
                            </li>
                            <li>
                                <GpsOffIcon
                                    color={AppStyle.mui.inherit}
                                    fontSize={AppWord.SMALL}
                                    style={{marginRight:10}}
                                />
                                24시간 이상 보고기록이 없음
                            </li>
                            <li>
                                <CellTowerIcon
                                    color={AppStyle.mui.disabled}
                                    fontSize={AppWord.SMALL}
                                    style={{marginRight:10}}
                                />
                                기지국의 위치(GPS 위성 신호 받지 못함)
                            </li>
                            <li>
                                <LocalParkingIcon
                                    color={AppStyle.mui.inherit}
                                    fontSize={AppWord.SMALL}
                                    style={{marginRight:10}}
                                />
                                30분 이상 보고기록이 없음
                            </li>
                            <li>
                                <GpsFixedIcon
                                    color={AppStyle.mui.success}
                                    fontSize={AppWord.SMALL}
                                    style={{marginRight:10}}
                                />
                                속도 2km/h 미만
                            </li>
                            <li>
                                <NavigationIcon
                                    color={AppStyle.mui.primary}
                                    fontSize={AppWord.SMALL}
                                    style={{marginRight:10}}
                                />
                                이동중 (방향 표시)
                            </li>
                        </ul>

                    </Box>
                </Popover>
            </HeaderBox>
            <FlexyTable
                name={RtdTermPosDictionary.tablePropsForMap.tableId}
                tableProps={RtdTermPosDictionary.tablePropsForMap}
                records={vehPosList}
                search={search}
                tools={tools}
                selected={[itemSelected]}
                onClickTool={onClickToolOnRow}
                onRowClicked={onClickRow}
                onEnterIndex={onMouseEnterTableIndexCell}
            />
        </VehPosListContainer>
    );
}